import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import jwtDecode from 'jwt-decode';
import { store } from 'redux/store';
import { JWTContent } from 'redux/reducers/authReducer';
import { InstructionTab } from 'types/Flux/About/getInstructionTabsResponse';
import GetInstructionPdfParams from 'types/Flux/About/getInstructionPdfParams';
import {
  AutomaticOptions,
  FluxScenariosArea,
  DailyDataWithRodadas,
  GetDailyDataParams,
  RawAutomaticOptions,
  transformAutomaticOptions,
  transformDailyDataResponse,
  PastSimulation,
  transformListPastSimulationResponse,
  SavePastSimulationParams,
  GetMonthlyDataResponse,
  GetMonthlyDataParams,
  GetRevisionDataParams,
  AutomaticRevisionData,
  RawRevisionDataPerScenario,
  GetRevisionScenarioParams,
  GetUnicenarioDataParams,
  UnicenarioSectionedData,
  GetUnicenarioScenarioParams,
  ClassicOptions,
  transformClassicOptionsResponse,
  GetClassicRevisionsParams,
  transformClassicRevisionsResponse,
  ClassicRevisionsData,
  GetClassicDataParams,
  ClassicData,
  transformClassicDataResponse,
} from 'helper/flux/automatic';
import {
  AutomaticPreference,
  AutomaticPreferenceType,
  SaveAutomaticPreferenceParams,
  VerifyZipParams,
  VerifyZipResponse,
  transformAutomaticPreferences,
  RawStatus,
  RawQueue,
  GetZipLinkParams,
  ZipLinkData,
  buildZipRequestBody,
} from 'helper/products/flux';
import {
  transformInstructionPdfResponse,
  transformInstructionTabsResponse,
} from 'helper/flux/about';
import getProductKey from 'helper/requests/getProductKey';
import baseApi, { GenericResponse, rawBaseQuery } from '../base';

type StatusResponse = GenericResponse<RawStatus[]>;

// --- Tipos VOBS ---
interface DadvazAvailableData {
  data_inicio: string;
  data_fim: string | null;
}

interface VobsDownloadData {
  url: string;
  filename: string;
  file_size_bytes: number | null;
  expires_in_hours: number;
}

interface PrevsAvailableData {
  ano: number;
  meses_disponiveis: {
    semanal: string[];
    mensal: string[];
  };
}

interface PrevsDownloadParams {
  ano: number;
  mes: number;
  tipo: 'semanal' | 'mensal';
}

type QueueStatusResponse = GenericResponse<RawQueue>;

export const fluxApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    reexecute: build.mutation<GenericResponse<any>, number>({
      query: (processID) => ({
        url: 'produtos/flux/re-execute',
        method: 'POST',
        body: {
          process_id: processID,
        },
      }),
      invalidatesTags: ['PersonalizedStudy', 'Study'],
    }),
    automaticOptions: build.query<AutomaticOptions, FluxScenariosArea | void>({
      queryFn: async (area, api, extraOptions) => {
        const productKey = getProductKey('prevs-automatico');
        const type = area || 'diaria';
        const url = `produtos/previvaz-automatico/get-options?product_key=${productKey}&type=${type}`;
        const res = await rawBaseQuery(url, api, extraOptions);
        const data = (res.data as GenericResponse<RawAutomaticOptions>)?.data;
        return {
          ...res,
          data: transformAutomaticOptions(data),
        } as QueryReturnValue<AutomaticOptions, unknown, unknown>;
      },
    }),
    getAutomaticPreferences: build.query<AutomaticPreference[], AutomaticPreferenceType>({
      query: (type) => {
        const userId = jwtDecode<JWTContent>(store.getState().auth.jwt).user_id;
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/preferencias-flux-auto-list/?product_key=${productKey}`,
          method: 'POST',
          body: { user_id: userId, type },
        };
      },
      transformResponse: transformAutomaticPreferences,
      providesTags: ['FaPreferences'],
    }),
    saveAutomaticPreference: build.mutation<any, SaveAutomaticPreferenceParams>({
      query: ({
        name, scenarios, type, spatialSubdivisionParam, acomph, mlt, pastSimulations,
      }) => {
        const userId = jwtDecode<JWTContent>(store.getState().auth.jwt).user_id;
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/preferencias-flux-auto-save?product_key=${productKey}`,
          method: 'POST',
          body: {
            id: 0,
            user_id: userId,
            name,
            preferencias_json: {
              data_acomph: acomph,
              data_previsao: mlt,
              versao: '',
              lista_cenarios: scenarios,
              lista_simulacoes_antigas: pastSimulations,
              ...spatialSubdivisionParam,
            },
            type,
          },
        };
      },
      invalidatesTags: ['FaPreferences'],
    }),
    getDailyData: build.query<DailyDataWithRodadas, GetDailyDataParams>({
      query: (params) => {
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/get-data-daily/?product_key=${productKey}`,
          method: 'POST',
          body: {
            data_acomph: params.data_acomph,
            data_previsao: params.data_previsao,
            versao: params.versao,
            lista_cenarios: params.lista_cenarios,
            incluir_acomph: params.incluir_acomph,
            incluir_mlt: params.incluir_mlt,
            lista_simulacoes_antigas: params.pastSimulations,
            ...params.spatialSubdivisionParam,
          },
        };
      },
      transformResponse: transformDailyDataResponse,
    }),
    verifyZip: build.query<VerifyZipResponse, VerifyZipParams>({
      query: (verifyZipParams) => ({
        url: 'produtos/previvaz-arquivo/verify-zip-file',
        method: 'POST',
        body: verifyZipParams,
      }),
    }),
    listPastSimulations: build.query<PastSimulation[], void>({
      query: () => {
        const userId = jwtDecode<JWTContent>(store.getState().auth.jwt).user_id;
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/simulacoes-antigas-list/?product_key=${productKey}`,
          method: 'POST',
          body: {
            user_id: userId,
          },
        };
      },
      transformResponse: transformListPastSimulationResponse,
      providesTags: ['PastSimulation'],
    }),
    savePastSimulation: build.mutation<GenericResponse<any>, SavePastSimulationParams>({
      query: ({
        id = 0, acomphDate, forecastDate, version, model,
      }: SavePastSimulationParams) => {
        const userId = jwtDecode<JWTContent>(store.getState().auth.jwt).user_id;
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/simulacoes-antigas-save?product_key=${productKey}`,
          method: 'POST',
          body: {
            id,
            user_id: userId,
            simulacao_json: {
              data_acomph: acomphDate,
              data_previsao: forecastDate,
              versao: version,
              modelo: model,
            },
          },
        };
      },
      invalidatesTags: ['PastSimulation'],
    }),
    deletePastSimulation: build.mutation<any, number>({
      query: (pastSimulationId: number) => {
        const userId = jwtDecode<JWTContent>(store.getState().auth.jwt).user_id;
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/simulacoes-antigas-delete?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            user_id: userId,
            id: pastSimulationId,
          },
        };
      },
      invalidatesTags: ['PastSimulation'],
    }),
    getPastSimulationsOptions: build.query<GenericResponse<any>, void>({
      query: () => {
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-automatico/get-list/?product_key=${productKey}`,
        };
      },
    }),
    deleteAutomaticPreference: build.mutation<any, number>({
      query: (preferenceId: number) => {
        const userId = jwtDecode<JWTContent>(store.getState().auth.jwt).user_id;
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/preferencias-flux-auto-delete?product_key=${productKey}`,
          method: 'DELETE',
          body: {
            user_id: userId,
            id: preferenceId,
          },
        };
      },
      invalidatesTags: ['FaPreferences'],
    }),
    getMonthlyData: build.query<GetMonthlyDataResponse, GetMonthlyDataParams>({
      query: ({
        data_acomph, data_previsao, versao, lista_cenarios, unidade,
        spatialSubdivisionParam, incluir_acomph, incluir_mlt, pastSimulations,
      }) => {
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/get-data-monthly/?product_key=${productKey}`,
          method: 'POST',
          body: {
            versao,
            unidade,
            data_acomph,
            data_previsao,
            lista_cenarios,
            incluir_acomph,
            incluir_mlt,
            lista_simulacoes_antigas: pastSimulations,
            ...spatialSubdivisionParam,
          },
        };
      },
    }),
    getRevisionData: build.query<GenericResponse<AutomaticRevisionData>, GetRevisionDataParams>({
      query: ({
        data_acomph, data_previsao, versao, ndiasprev, lista_cenarios, unidade,
        spatialSubdivisionParam, pastSimulations, revision,
      }) => {
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/rvx-acompanhamento/?product_key=${productKey}`,
          method: 'POST',
          body: {
            versao,
            unidade,
            data_acomph,
            data_previsao,
            ndiasprev,
            lista_cenarios,
            revisao: revision,
            lista_simulacoes_antigas: pastSimulations,
            ...spatialSubdivisionParam,
          },
        };
      },
    }),
    getRevisionPerScenarios: build.query<RawRevisionDataPerScenario, GetRevisionScenarioParams>({
      query: ({
        data_acomph, data_previsao, versao, lista_cenarios, unidade,
        spatialSubdivisionParam, revision,
      }) => {
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/rvx/?product_key=${productKey}`,
          method: 'POST',
          body: {
            versao,
            unidade,
            data_acomph,
            data_previsao,
            lista_cenarios,
            revisao: revision,
            ...spatialSubdivisionParam,
          },
        };
      },
    }),
    getRevisionOptions: build.query<GenericResponse<string[]>, void>({
      query: () => {
        const productKey = getProductKey('prevs-automatico');
        const url = `produtos/previvaz-ena-diaria/rvx-options/?product_key=${productKey}`;
        return { url };
      },
    }),
    getUnicenarioData: build
      .query<GenericResponse<UnicenarioSectionedData>, GetUnicenarioDataParams>({
      query: ({
        data_acomph, data_previsao, versao, ndiasprev, cenario, unidade, revision,
      }) => {
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/rvx-unicenario-acompanhamento/?product_key=${productKey}`,
          method: 'POST',
          body: {
            versao,
            unidade,
            data_acomph,
            data_previsao,
            ndiasprev,
            cenario,
            revisao: revision,
          },
        };
      },
    }),
    getUnicenarioPerScenarios: build
      .query<RawRevisionDataPerScenario, GetUnicenarioScenarioParams>({
      query: ({
        data_acomph, data_previsao, versao, cenario, unidade, revision,
      }) => {
        const productKey = getProductKey('prevs-automatico');

        return {
          url: `produtos/previvaz-ena-diaria/rvx-unicenario/?product_key=${productKey}`,
          method: 'POST',
          body: {
            versao,
            unidade,
            data_acomph,
            data_previsao,
            cenario,
            revisao: revision,
          },
        };
      },
    }),
    getClassicOptions: build.query<ClassicOptions, void>({
      query: () => {
        const productKey = getProductKey('prevs-automatico');
        const url = `produtos/previvaz-automatico/get-list/?product_key=${productKey}`;
        return { url };
      },
      transformResponse: transformClassicOptionsResponse,
    }),
    getClassicRevisions: build.query<ClassicRevisionsData, GetClassicRevisionsParams>({
      query: ({ acomphDateLabel, forecastDateLabel, model }) => {
        const productKey = getProductKey('prevs-automatico');
        return {
          url: `produtos/previvaz-automatico/rev-options/?product_key=${productKey}`,
          method: 'POST',
          body: {
            acomph: acomphDateLabel,
            data_prev: forecastDateLabel,
            modelos: model,
          },
        };
      },
      transformResponse: transformClassicRevisionsResponse,
    }),
    getClassicData: build.query<ClassicData, GetClassicDataParams>({
      query: (params) => {
        const productKey = getProductKey('prevs-automatico');
        const {
          acomphDateLabel,
          forecastDateLabel,
          model,
          revision,
        } = params;
        return {
          url: `produtos/previvaz-automatico/result-table/?product_key=${productKey}`,
          method: 'POST',
          body: {
            acomph: acomphDateLabel,
            data_prev: forecastDateLabel,
            modelos: model,
            revisao: revision,
          },
        };
      },
      transformResponse: transformClassicDataResponse,
    }),
    getFluxStatus: build.query<StatusResponse, void>({
      query: () => ({
        url: 'produtos/previvaz-automatico/verify-last-results-automatico',
        method: 'POST',
        body: {},
      }),
    }),
    getQueueStatus: build.query<QueueStatusResponse, void>({
      query: () => ({
        url: 'produtos/flux/get-queue-status',
        method: 'POST',
        body: {},
      }),
    }),
    getInstructionTabs: build.query<InstructionTab[], void>({
      query: () => ({
        url: 'produtos/flux/instructions_tabs',
        method: 'GET',
      }),
      transformResponse: transformInstructionTabsResponse,
    }),
    getInstructionPdf: build.query<string, GetInstructionPdfParams>({
      query: ({ id }) => ({
        url: 'produtos/flux/instructions_pdf',
        method: 'POST',
        body: { id },
      }),
      transformResponse: transformInstructionPdfResponse,
    }),
    // --- VOBS ---
    vobsDadvazAvailable: build.query<DadvazAvailableData, void>({
      queryFn: async (_, api, extraOptions) => {
        const productKey = getProductKey('prevs-automatico');
        const url = `produtos/flux/vobs/dadvaz/available/?product_key=${productKey}`;
        const res = await rawBaseQuery(url, api, extraOptions);
        const data = (res.data as GenericResponse<DadvazAvailableData>)?.data;
        return { ...res, data } as QueryReturnValue<DadvazAvailableData, unknown, unknown>;
      },
    }),
    vobsDadvazDownload: build.mutation<VobsDownloadData, { data: string }>({
      queryFn: async (body, api, extraOptions) => {
        const productKey = getProductKey('prevs-automatico');
        const url = `produtos/flux/vobs/dadvaz/download/?product_key=${productKey}`;
        const res = await rawBaseQuery({ url, method: 'POST', body }, api, extraOptions);
        const data = (res.data as GenericResponse<VobsDownloadData>)?.data;
        return { ...res, data } as QueryReturnValue<VobsDownloadData, unknown, unknown>;
      },
    }),
    vobsPrevsAvailable: build.query<PrevsAvailableData, { ano: number }>({
      queryFn: async ({ ano }, api, extraOptions) => {
        const productKey = getProductKey('prevs-automatico');
        const url = `produtos/flux/vobs/prevs/available/?ano=${ano}&product_key=${productKey}`;
        const res = await rawBaseQuery(url, api, extraOptions);
        const data = (res.data as GenericResponse<PrevsAvailableData>)?.data;
        return { ...res, data } as QueryReturnValue<PrevsAvailableData, unknown, unknown>;
      },
    }),
    vobsPrevsDownload: build.mutation<VobsDownloadData, PrevsDownloadParams>({
      queryFn: async (body, api, extraOptions) => {
        const productKey = getProductKey('prevs-automatico');
        const url = `produtos/flux/vobs/prevs/download/?product_key=${productKey}`;
        const res = await rawBaseQuery({ url, method: 'POST', body }, api, extraOptions);
        const data = (res.data as GenericResponse<VobsDownloadData>)?.data;
        return { ...res, data } as QueryReturnValue<VobsDownloadData, unknown, unknown>;
      },
    }),
    // Link curto para o navegador baixar o ZIP direto no disco (BE #58 / FE #1957). `query` (e não
    // queryFn) para o baseQuery tratar `code !== 200` e recuperar o product_key vencido.
    getZipLink: build.mutation<ZipLinkData, GetZipLinkParams>({
      query: ({ nomeArquivo, ...params }) => ({
        url: `produtos/previvaz-ena-diaria/get-zip-link/?product_key=${getProductKey('prevs-automatico')}`,
        method: 'POST',
        body: { ...buildZipRequestBody(params), nome_arquivo: nomeArquivo },
      }),
      transformResponse: (response: GenericResponse<ZipLinkData>) => response.data,
    }),
  }),
});

export const {
  useReexecuteMutation,
  useAutomaticOptionsQuery,
  useGetAutomaticPreferencesQuery,
  useSaveAutomaticPreferenceMutation,
  useVerifyZipQuery,
  useLazyVerifyZipQuery,
  useGetDailyDataQuery,
  useLazyGetDailyDataQuery,
  useListPastSimulationsQuery,
  useSavePastSimulationMutation,
  useGetPastSimulationsOptionsQuery,
  useDeleteAutomaticPreferenceMutation,
  useLazyGetMonthlyDataQuery,
  useLazyGetRevisionDataQuery,
  useLazyGetRevisionPerScenariosQuery,
  useGetRevisionOptionsQuery,
  useLazyGetUnicenarioDataQuery,
  useLazyGetUnicenarioPerScenariosQuery,
  useGetClassicOptionsQuery,
  useLazyGetClassicRevisionsQuery,
  useLazyGetClassicDataQuery,
  useGetFluxStatusQuery,
  useLazyGetFluxStatusQuery,
  useGetQueueStatusQuery,
  useLazyGetQueueStatusQuery,
  useDeletePastSimulationMutation,
  useGetInstructionTabsQuery,
  useGetInstructionPdfQuery,
  useLazyGetInstructionPdfQuery,
  useVobsDadvazAvailableQuery,
  useVobsDadvazDownloadMutation,
  useVobsPrevsAvailableQuery,
  useVobsPrevsDownloadMutation,
  useGetZipLinkMutation,
} = fluxApi;
